<template>
  <BaseDropdown
    v-if="isSuperUser"
    v-loading="isFetching"
    :options="options"
    :label="selectedOption"
    class="mr-2"
    toggle-class="nav-link btn-outline-white btn-sm px-2 px-sm-3"
  >
    <template v-slot:first>
      <div class="py-2 px-3">
        <BaseSearchInput v-model="search" />
      </div>
    </template>

    <template v-slot:default="{ close }">
      <VirtualScroll
        v-if="!!filteredOptions.length"
        :items="filteredOptions"
        @click:item="(item) => handleSelectOption(item.value, close)"
      >
        <template v-slot:item="item">
          <span
            class="py-1 px-3 d-block"
            :class="{
              'text-white bg-primary': item.value === bdxFilters.companyId
            }"
            >{{ item.text }}</span
          >
        </template>
      </VirtualScroll>
      <div v-else class="py-2 px-3 small">No companies found.</div>
    </template>
  </BaseDropdown>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import Fuse from 'fuse.js'

import { useTryCatch, useCurrentUser } from '@composables'
import { CompanyService } from '@services'

import { BaseDropdown, BaseSearchInput } from '@components/base'
import { VirtualScroll } from '@components/features'

export default defineComponent({
  name: 'CompanyListDropdown',
  components: {
    BaseDropdown,
    BaseSearchInput,

    VirtualScroll
  },
  emits: ['change'],
  setup() {
    const { useGetters, useMutations } = createVuexHelpers()

    const { bdxFilters } = useGetters(['bdxFilters'])
    const { SET_BDX_FILTER, UNSET_BDX_FILTER } = useMutations([
      'SET_BDX_FILTER',
      'UNSET_BDX_FILTER'
    ])

    const { isSuperUser } = useCurrentUser()

    const search = ref('')

    // Data fetching
    const isFetching = ref(false)

    const options = ref([{ text: 'All', value: null }])

    const filteredOptions = computed(() => {
      if (!search.value) return options.value

      const fuse = new Fuse(options.value, {
        keys: ['text'],
        useExtendedSearch: true
      })

      return fuse.search(`'${search.value}`).map((item) => item.item)
    })

    const handleFetch = async () => {
      if (!isSuperUser.value) return

      isFetching.value = true

      await useTryCatch(async () => {
        const { data } = await CompanyService.list({
          page_size: 1000
        })
        const { data: response } = data || {}

        const newOptions = response.results.map((result) => ({
          value: result.id,
          text: result.name
        }))

        options.value = options.value.concat(newOptions)
      })

      isFetching.value = false
    }

    const selectedOption = computed(() => {
      if (!bdxFilters.value.companyId) return 'All'

      const { text } =
        options.value.find(
          (option) => option.value === bdxFilters.value.companyId
        ) || {}

      return text || 'All'
    })

    const handleSelectOption = async (value, callback = undefined) => {
      isFetching.value = true

      await useTryCatch(async () => {
        if (!value || value === bdxFilters.value.companyId) {
          UNSET_BDX_FILTER('companyId')

          SET_BDX_FILTER({
            key: 'companyRoleName',
            value: 'admin'
          })
          SET_BDX_FILTER({
            key: 'companyRoleValue',
            value: 'admin'
          })
        } else {
          const { data } = await CompanyService.read(value)
          const { data: results } = data || {}

          SET_BDX_FILTER({
            key: 'companyId',
            value
          })
          SET_BDX_FILTER({
            key: 'companyRoleName',
            value: results.data.role.role
          })
          SET_BDX_FILTER({
            key: 'companyRoleValue',
            value: results.data.role_value
          })
        }

        if (!!callback) callback()
      })

      isFetching.value = false
    }

    onMounted(handleFetch)

    return {
      isSuperUser,

      bdxFilters,

      search,

      options,
      filteredOptions,
      selectedOption,
      handleSelectOption,

      isFetching
    }
  }
})
</script>

<style scoped>
.dropdown >>> .dropdown-toggle {
  min-width: 140px;
}

.dropdown >>> .dropdown-menu {
  min-width: 300px;
}

.dropdown >>> .el-loading-spinner {
  width: 24px !important;
  margin-top: -12px !important;
}

.dropdown >>> .el-loading-spinner .circular {
  width: 24px !important;
  height: 24px !important;
}

.dropdown >>> .el-loading-spinner .path {
  stroke: #fff !important;
}
</style>
